import { useState } from 'react';
import { ApiKey } from '../../services/api';

interface Props {
  apiKeys: ApiKey[];
  onGenerate: () => void;
  onRevoke: (id: string) => void;
  isRevoking: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getSessionKey(id: string): string | null {
  return sessionStorage.getItem(`api_key_${id}`);
}

export function ApiKeysTab({ apiKeys, onGenerate, onRevoke, isRevoking }: Props) {
  const [copied, setCopied] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  async function handleCopy(id: string) {
    const key = getSessionKey(id);
    if (!key) return;
    await navigator.clipboard.writeText(key);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  function handleToggleView(id: string) {
    setViewing((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">API Keys</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Use API keys to authenticate external integrations (Zapier, Make, custom scripts).
            Keys are workspace-scoped and never expire unless revoked.
          </p>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          className="shrink-0 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          + Generate new key
        </button>
      </div>

      {apiKeys.length === 0 ? (
        <div className="rounded-lg border border-border-subtle bg-background-secondary px-6 py-10 text-center">
          <p className="text-sm text-text-secondary">
            No API keys yet. Generate one to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border-subtle overflow-hidden">
          <table className="min-w-full divide-y divide-border-subtle">
            <thead className="bg-background-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Key
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Last used
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle bg-surface-default">
              {apiKeys.map((k) => {
                const sessionKey = getSessionKey(k.id);
                return (
                  <tr key={k.id}>
                    <td className="px-4 py-3 text-sm font-medium text-text-primary">
                      {k.name}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {sessionKey && viewing === k.id ? (
                          <code className="text-xs font-mono text-text-primary bg-background-secondary px-2 py-1 rounded break-all">
                            {sessionKey}
                          </code>
                        ) : (
                          <code className="text-xs font-mono text-text-secondary bg-background-secondary px-2 py-1 rounded">
                            {k.keyPrefix}…
                          </code>
                        )}
                        {sessionKey && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleToggleView(k.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                            >
                              {viewing === k.id ? 'Hide' : 'View'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopy(k.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                            >
                              {copied === k.id ? 'Copied!' : 'Copy'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {formatDate(k.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {formatDate(k.lastUsedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onRevoke(k.id)}
                        disabled={isRevoking === k.id}
                        className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                      >
                        {isRevoking === k.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
